import { useState } from "react";
import { Trash2, Plus, ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import type {
  Channel,
  ContentInput,
  ContentItem,
  ContentStatus,
} from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import {
  CONTENT_STATUSES,
  CONTENT_FORMATS,
  fmtDateInput,
  parseDateInput,
  publishUrgency,
  type ContentGroup,
  type GroupBy,
} from "@/lib/content";
import { PILLARS } from "@/lib/pmbok";
import { cn } from "@/lib/utils";

interface Props {
  groups: ContentGroup[];
  channels: Channel[];
  canEdit: boolean;
  groupBy: GroupBy;
  onUpdate: (id: number, patch: ContentInput) => Promise<unknown>;
  onDelete: (id: number) => Promise<unknown>;
  /** Add a blank item to this group (pre-filled with the group's channel/pillar). */
  onAddToGroup?: (group: ContentGroup) => void;
  /** Item click → open the full editor. */
  onOpenItem?: (item: ContentItem) => void;
  /** Manual reorder: the group's item ids in their new order. */
  onReorder?: (orderedIds: number[]) => void;
}

const STATUS_TONE: Record<ContentStatus, string> = {
  idea:      "border-border bg-background text-muted-foreground",
  drafting:  "border-accent/60 bg-accent/10 text-foreground",
  scheduled: "border-primary/60 bg-primary/10 text-foreground",
  published: "border-success/60 bg-success/10 text-foreground",
  evergreen: "border-secondary/60 bg-secondary/10 text-foreground",
  killed:    "border-border bg-muted text-muted-foreground line-through",
};

const URGENCY_TONE = {
  ontrack: "text-muted-foreground",
  soon: "text-accent-foreground",
  late: "text-destructive",
} as const;

export function ContentList({
  groups,
  channels,
  canEdit,
  groupBy,
  onUpdate,
  onDelete,
  onAddToGroup,
  onOpenItem,
  onReorder,
}: Props) {
  // Groups are accordions: collapsed by default, each toggles in place.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const expand = (key: string) => setOpen((prev) => new Set(prev).add(key));
  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <Card
          key={g.key}
          className="overflow-hidden border-l-4"
          style={{ borderLeftColor: g.color ?? "hsl(var(--primary))" }}
        >
          <div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2.5">
            <button
              type="button"
              onClick={() => toggle(g.key)}
              aria-expanded={open.has(g.key)}
              className="-ml-1 flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-0.5 text-left hover:bg-background"
            >
              {open.has(g.key) ? (
                <ChevronDown size={15} className="shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight size={15} className="shrink-0 text-muted-foreground" />
              )}
              {groupBy === "channel" && g.color ? (
                <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: g.color }} aria-hidden />
              ) : null}
              <h3 className="truncate text-sm font-semibold">{g.label}</h3>
            </button>
            <div className="flex shrink-0 items-center gap-2">
              {canEdit && onAddToGroup ? (
                <button
                  type="button"
                  onClick={() => {
                    expand(g.key);
                    onAddToGroup(g);
                  }}
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
                >
                  <Plus size={12} /> Add
                </button>
              ) : null}
              <span className="rounded-full bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground tabular-nums">
                {g.items.length}
              </span>
            </div>
          </div>
          {open.has(g.key) ? (
          <CardContent className="p-0">
            {g.items.map((it, i) => {
              // Only offer a move where it sticks: the sort respects sort_order
              // within a same-status run, so reorder against a same-status neighbor.
              const canUp = canEdit && !!onReorder && i > 0 && g.items[i - 1].status === it.status;
              const canDown =
                canEdit && !!onReorder && i < g.items.length - 1 && g.items[i + 1].status === it.status;
              const move = (dir: -1 | 1) => {
                const arr = g.items.slice();
                const j = i + dir;
                [arr[i], arr[j]] = [arr[j], arr[i]];
                onReorder!(arr.map((x) => x.id));
              };
              return (
                <ContentRow
                  key={it.id}
                  item={it}
                  channels={channels}
                  canEdit={canEdit}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  onOpenItem={onOpenItem}
                  onMoveUp={canUp ? () => move(-1) : undefined}
                  onMoveDown={canDown ? () => move(1) : undefined}
                />
              );
            })}
          </CardContent>
          ) : null}
        </Card>
      ))}
    </div>
  );
}

function ContentRow({
  item,
  channels,
  canEdit,
  onUpdate,
  onDelete,
  onOpenItem,
  onMoveUp,
  onMoveDown,
}: {
  item: ContentItem;
  channels: Channel[];
  canEdit: boolean;
  onUpdate: (id: number, patch: ContentInput) => Promise<unknown>;
  onDelete: (id: number) => Promise<unknown>;
  onOpenItem?: (item: ContentItem) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const urgency =
    item.status === "scheduled" && item.scheduled_for != null
      ? publishUrgency(item.scheduled_for)
      : null;

  return (
    <div className="flex flex-col gap-2 border-b px-3 py-3 last:border-b-0 sm:flex-row sm:items-start">
      {/* Status */}
      <select
        value={item.status}
        disabled={!canEdit}
        onChange={(e) => void onUpdate(item.id, { status: e.target.value as ContentStatus })}
        className={cn(
          "h-7 shrink-0 rounded-full border px-2 text-xs font-medium outline-none sm:w-28",
          STATUS_TONE[item.status],
          !canEdit && "pointer-events-none opacity-80",
        )}
      >
        {CONTENT_STATUSES.map((s) => (
          <option key={s.slug} value={s.slug}>
            {s.label}
          </option>
        ))}
      </select>

      {/* Title + meta */}
      <div className="min-w-0 flex-1">
        {onOpenItem ? (
          <button
            type="button"
            onClick={() => onOpenItem(item)}
            title="Open editor"
            className="block w-full px-3 py-2 text-left text-sm font-medium transition-colors hover:text-primary"
          >
            {item.title}
          </button>
        ) : (
          <div className="px-3 py-2 text-sm font-medium">{item.title}</div>
        )}

        <div className="flex flex-wrap items-center gap-1.5 pl-3 pt-1">
          <MetaSelect
            value={item.channel_id == null ? "" : String(item.channel_id)}
            disabled={!canEdit}
            placeholder="No integration"
            onChange={(v) =>
              void onUpdate(item.id, { channel_id: v === "" ? null : Number(v) })
            }
            options={channels.map((c) => ({ value: String(c.id), label: c.name }))}
          />
          <MetaSelect
            value={item.pillar ?? ""}
            disabled={!canEdit}
            placeholder="No pillar"
            onChange={(v) =>
              void onUpdate(item.id, { pillar: v === "" ? null : (v as ContentItem["pillar"]) })
            }
            options={PILLARS.map((p) => ({ value: p.slug, label: p.label }))}
          />
          <MetaSelect
            value={item.format}
            disabled={!canEdit}
            onChange={(v) => void onUpdate(item.id, { format: v as ContentItem["format"] })}
            options={CONTENT_FORMATS.map((f) => ({ value: f.slug, label: f.label }))}
          />
          {item.piece ? (
            <span
              title={item.piece}
              className="inline-flex h-7 items-center rounded-md border border-border bg-background px-1.5 text-[11px] text-muted-foreground"
            >
              <span className="max-w-[12rem] truncate">{item.piece}</span>
            </span>
          ) : null}
          {item.provenance === "repost" ? (
            <span className="inline-flex h-7 items-center rounded-md border border-secondary/60 bg-secondary/10 px-1.5 text-[11px] text-foreground">
              Repost
            </span>
          ) : null}
        </div>
      </div>

      {/* Reorder + schedule + delete */}
      <div className="flex shrink-0 items-center gap-2 pl-3 sm:pl-0">
        {onMoveUp || onMoveDown ? (
          <div className="flex flex-col">
            <button
              type="button"
              aria-label="Move up"
              disabled={!onMoveUp}
              onClick={onMoveUp}
              className="grid h-4 w-4 place-items-center text-muted-foreground/50 transition-colors hover:text-foreground disabled:opacity-25"
            >
              <ChevronUp size={13} />
            </button>
            <button
              type="button"
              aria-label="Move down"
              disabled={!onMoveDown}
              onClick={onMoveDown}
              className="grid h-4 w-4 place-items-center text-muted-foreground/50 transition-colors hover:text-foreground disabled:opacity-25"
            >
              <ChevronDown size={13} />
            </button>
          </div>
        ) : null}
        <div className="flex flex-col items-end">
          <input
            type="date"
            value={fmtDateInput(item.scheduled_for)}
            disabled={!canEdit}
            onChange={(e) => void onUpdate(item.id, { scheduled_for: parseDateInput(e.target.value) })}
            className="h-7 rounded-md border border-input bg-background px-1.5 text-xs text-muted-foreground outline-none disabled:opacity-70"
          />
          {urgency ? (
            <span className={cn("pt-0.5 text-[10px] tabular-nums", URGENCY_TONE[urgency.tone])}>
              {urgency.label}
            </span>
          ) : null}
        </div>
        {canEdit ? (
          <button
            type="button"
            aria-label="Delete"
            onClick={() => {
              if (confirm(`Delete "${item.title}"?`)) void onDelete(item.id);
            }}
            className="grid size-7 place-items-center rounded-md text-muted-foreground/60 transition-colors hover:bg-muted hover:text-destructive"
          >
            <Trash2 size={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function MetaSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "h-7 max-w-[9rem] rounded-md border border-border bg-background px-1.5 text-[11px] text-muted-foreground outline-none",
        disabled && "pointer-events-none opacity-80",
      )}
    >
      {placeholder != null ? <option value="">{placeholder}</option> : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
